<template>
    <div class="country-container" :class="{ 'flex-grow': props.fullWidth }">
        <div v-if="mainCountries.length">
            <prime-select
                :class="{ 'full-width': props.fullWidth }"
                :model-value="selectedCountry"
                :input-id="props.inputId"
                :options="mainCountries"
                option-label="name"
                option-disabled="isDisabled"
                :placeholder="props.isSiteWideCountryDefault ? 'None' : 'Select Country'"
                :aria-label="props.ariaLabel"
                auto-filter-focus
                filter
                :disabled="props.disabled"
                :show-clear="props.isSiteWideCountryDefault"
                @update:model-value="updateSelectedCountry"
                @hide="setIsOpen(false)"
                @before-show="setIsOpen(true)"
            >
                <template #dropdownicon>
                    <font-awesome-icon class="dropdown-icon" :class="{ inverted: isOpen }" :icon="['fas', 'chevron-down']" />
                </template>
                <template #option="{ option }">
                    <div class="country-option-wrapper" :class="{ 'sub-country': option.isSubCountry, parent: option.subCountries?.length }">
                        <div class="country-option" :class="{ disabled: option.isDisabled }">
                            <div v-if="option.shortName" class="flag">
                                <country-flag :iso-code="option.shortName.toLowerCase()" />
                            </div>
                            {{ option.name }}
                        </div>
                        <font-awesome-icon v-if="option.isDisabled" :icon="['fas', 'lock-keyhole']" />
                    </div>
                </template>
                <template #value="{ value }">
                    <div v-if="value" class="selection-option">
                        <div v-if="value.shortName" class="flag" :aria-label="value.name" :title="value.name">
                            <country-flag :iso-code="value.shortName.toLowerCase()" />
                        </div>
                        <span>{{ value.name }}</span>
                    </div>
                </template>
            </prime-select>
        </div>
        <prime-select
            v-if="subCountries.length && props.allowInternalCountry"
            :model-value="selectedSubCountry"
            :options="subCountries"
            option-label="name"
            placeholder="Select Country"
            :input-id="subCountryInputId"
            :aria-label="props.ariaLabel ? `${props.ariaLabel} region` : undefined"
            auto-filter-focus
            filter
            :disabled="props.disabled"
            @update:model-value="updateSelectedSubCountry"
        />
    </div>
</template>

<script lang="ts" setup>
import PrimeSelect from 'primevue/select';
import { computed, ref, watch } from 'vue';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { ICountry } from '@/interfaces/general/ICountry.ts';
import CountryFlag from '@/components/powerMarkets/shared/CountryFlag.vue';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useUserSettingsActions } from '@/actions/powerMarkets/userSettingsActions.ts';
import { countryHasAccess, countryIdMatches, findCountryByCountryId } from '@/helpers/powerMarkets/countrySelectorOptions.ts';

const dataStore = useDataStore();
const userSettingsStore = useUserSettingsStore();
const userSettingsActions = useUserSettingsActions();

const props = defineProps<{
    modelValue?: IIdName | null;
    allowedCountries?: Array<string>;
    accessByCountryId?: Record<string, boolean>;
    allowInternalCountry?: boolean;
    disabled?: boolean;
    doNotEmitOnCreate?: boolean;
    enableAllCountries?: boolean;
    europeAccess?: boolean;
    fullWidth?: boolean;
    specificCountryAccess?: boolean;
    isSiteWideCountryDefault?: boolean;
    ariaLabel?: string;
    inputId?: string;
    autoSelectFirstSubCountry?: boolean;
}>();

const subCountryInputId = computed<string | undefined>(() => (
    props.inputId ? `${props.inputId}-region` : undefined
));

const emit = defineEmits<{
    'update:modelValue': [IIdName];
}>();

const selectedCountry = ref<ICountry | null>(null);
const selectedSubCountry = ref<IIdName | null>(null);
const isOpen = ref<boolean>(false);

const mainCountries = computed<Array<ICountry>>(() => {
    let countries = dataStore.staticStore.countries;
    const accessProperty = getAccessProperty();

    if (props.allowedCountries && props.allowedCountries.length > 0) {
        countries = countries.filter(country => props.allowedCountries!.some(allowedCountryId => countryMatchesAllowedCountryId(country, allowedCountryId)));
    }

    countries.forEach(country => {
        const hasAccess = props.accessByCountryId
            ? countryHasAccess(country, props.accessByCountryId)
            : country[accessProperty];
        country.isDisabled = !props.enableAllCountries && !hasAccess;
    });

    return countries;
});

const subCountries = computed(() => {
    if (!selectedCountry.value || !selectedCountry.value.subCountries.length) return [];
    return getAllowedSubCountries(selectedCountry.value);
});

function countryMatchesAllowedCountryId (country: ICountry, allowedCountryId: string): boolean {
    return countryIdMatches(country.id, allowedCountryId)
        || country.subCountries.some(subCountry => countryIdMatches(subCountry.id, allowedCountryId));
}

function getAccessProperty (): keyof ICountry {
    if (props.specificCountryAccess) return 'hasSpecificAccess';
    if (props.europeAccess) return 'hasEuropeAccess';
    return 'hasAccess';
}

function getAllowedSubCountries (country: ICountry): Array<IIdName> {
    if (!props.allowedCountries || props.allowedCountries.length === 0) return country.subCountries;
    if (props.allowedCountries.some(allowedCountryId => countryIdMatches(country.id, allowedCountryId))) return country.subCountries;

    return country.subCountries.filter(subCountry => props.allowedCountries!.some(allowedCountryId => countryIdMatches(subCountry.id, allowedCountryId)));
}

function setIsOpen (value: boolean): void {
    isOpen.value = value;
}

function emitCountryId (): void {
    if (!selectedCountry.value) {
        if (props.isSiteWideCountryDefault) {
            const countryToEmit = {
                id: 'None',
                name: 'None',
            };

            emit('update:modelValue', countryToEmit);
        }
        return;
    }

    if (props.allowInternalCountry && selectedCountry.value.subCountries.length) {
        // Emit parent when no sub-country is chosen yet so consumers (e.g. series
        // filters) clear options keyed on zone ids, matching Enact behaviour.
        emit('update:modelValue', selectedSubCountry.value ?? selectedCountry.value);
        return;
    }

    emit('update:modelValue', selectedCountry.value);
}

function updateSelectedCountry (updatedCountry: ICountry): void {
    selectedCountry.value = updatedCountry;

    if (props.autoSelectFirstSubCountry && updatedCountry.subCountries.length) {
        selectedSubCountry.value = subCountries.value.find(c => c.id === updatedCountry.mainSubCountryId) ?? subCountries.value[0];
    } else {
        selectedSubCountry.value = null;
    }

    emitCountryId();
    if (!props.isSiteWideCountryDefault) setDefaultCountryFallback();
}

function updateSelectedSubCountry (updatedSubCountry: IIdName): void {
    selectedSubCountry.value = updatedSubCountry;
    emitCountryId();
    if (!props.isSiteWideCountryDefault) setDefaultCountryFallback();
}

function setDefaultCountryFallback (): void {
    const countryString = selectedCountry.value?.mainSubCountryId || selectedCountry.value?.id || 'Gb';
    userSettingsActions.updateFallbackCountry(countryString);
}

function setFieldsFromProps (): void {
    if (props.modelValue === null) {
        selectedCountry.value = null;
        return;
    }

    const savedCountry = props.isSiteWideCountryDefault ? userSettingsStore.userSettings.otherSettings.data.country : userSettingsStore.userCountry;
    const countryIdToUse = props.modelValue?.id || savedCountry;
    selectedCountry.value = findCountryByCountryId(dataStore.staticStore.countries, countryIdToUse) ?? null;

    if (selectedCountry.value && props.allowInternalCountry) {
        selectedSubCountry.value = selectedCountry.value.subCountries.find(c => countryIdMatches(c.id, countryIdToUse)) ?? null;
    }
}

function initialise (): void {
    setFieldsFromProps();

    if (!props.doNotEmitOnCreate) {
        emitCountryId();
    }
}

initialise();

watch(() => props.modelValue, () => {
    setFieldsFromProps();
});

</script>

<style lang="scss" scoped>

.country-container {
    display: flex;
    gap: var(--defaultSpacing);
    align-content: start;

    &.flex-grow {
        flex-grow: 1;
        margin-right: 0;
    }

    .full-width {
        width: 100%;
    }
}

.country-option-wrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;

    &.parent {
        pointer-events: none;
        user-select: none;
        cursor: default;
    }

    &.sub-country {
        margin-left: 5px;
    }

    .country-option {
        align-items: center;
        display: flex;
        gap: var(--defaultSpacing);

        &.disabled {
            pointer-events: none;
            filter: grayscale(1);
        }

        .flag {
            height: 2rem;
            position: relative;
            width: 2.5rem;
        }
    }
}

.selection-option {
    align-items: center;
    display: flex;
    gap: var(--defaultSpacing);

    .flag {
        height: var(--defaultFontSize);
        width: 2rem;
    }
}

.dropdown-icon {
    font-size: var(--smallFontSize);

    &.inverted {
        transform: rotate(180deg);
    }
}

</style>
